import React, { useState, useEffect, useCallback } from 'react';
import { Header } from '../../components/layout';
import { Button, Modal } from '../../components/common';
import {
  Check,
  Plus,
  PencilSimple,
  Trash,
  Power,
  FloppyDisk,
  Gear,
  Clock
} from '@phosphor-icons/react';
import slotService from '../../services/slotService';
import './Slots.css';

export function Slots({ onMenuClick }) {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [pickupSlots, setPickupSlots] = useState([]);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [editingSlot, setEditingSlot] = useState(null);
  const [tempCapacity, setTempCapacity] = useState('');
  const [savedSlot, setSavedSlot] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Edit Slots Modal State
  const [showEditSlotsModal, setShowEditSlotsModal] = useState(false);
  const [allSlots, setAllSlots] = useState([]);
  const [loadingAllSlots, setLoadingAllSlots] = useState(false);
  const [showAddSlotForm, setShowAddSlotForm] = useState(false);
  const [editingSlotData, setEditingSlotData] = useState(null);
  const [slotFormData, setSlotFormData] = useState({
    slotName: '',
    slotType: 'Pickup',
    maxCapacity: 10
  });
  const [savingSlot, setSavingSlot] = useState(false);
  const [deletingSlotId, setDeletingSlotId] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [modalSuccess, setModalSuccess] = useState(null);

  const formatSelectedDate = (dateStr) => {
    const [y, m, d] = String(dateStr || '').split('-').map(Number);
    if (!y || !m || !d) return dateStr;
    return new Date(y, m - 1, d).toLocaleDateString('en-IN', { dateStyle: 'medium' });
  };

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const slots = await slotService.getSlotStatus(selectedDate);

      const byOrder = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.slotName).localeCompare(String(b.slotName));

      // Transform and separate by slot type (active slots only on daily status)
      const pickup = slots
        .filter(slot => slot.slotType === 'Pickup' && slot.isActive !== false)
        .sort(byOrder)
        .map(slot => ({
          id: slot._id,
          name: slot.slotName,
          used: slot.bookedCount || 0,
          capacity: slot.maxCapacity,
          isActive: slot.isActive !== false
        }));

      const delivery = slots
        .filter(slot => slot.slotType === 'Delivery' && slot.isActive !== false)
        .sort(byOrder)
        .map(slot => ({
          id: slot._id,
          name: slot.slotName,
          used: slot.bookedCount || 0,
          capacity: slot.maxCapacity,
          isActive: slot.isActive !== false
        }));

      setPickupSlots(pickup);
      setDeliverySlots(delivery);
    } catch (err) {
      console.error('Failed to fetch slots:', err);
      setError('Failed to load slots. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const handleSeedSlots = async () => {
    setSeeding(true);
    setError(null);
    try {
      const result = await slotService.seedSlots();
      setSuccess(result.message || 'Slots created successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchSlots();
    } catch (err) {
      console.error('Failed to seed slots:', err);
      setError(err.response?.data?.message || 'Failed to create slots. Please try again.');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Fetch all slots for edit modal
  const fetchAllSlots = useCallback(async () => {
    setLoadingAllSlots(true);
    try {
      const slots = await slotService.getSlots();
      const sorted = [...(slots || [])].sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.slotName).localeCompare(String(b.slotName))
      );
      setAllSlots(sorted);
    } catch (err) {
      console.error('Failed to fetch all slots:', err);
      setError('Failed to load slots for editing.');
    } finally {
      setLoadingAllSlots(false);
    }
  }, []);

  // Open edit slots modal
  const handleOpenEditSlots = () => {
    setModalError(null);
    setModalSuccess(null);
    setShowEditSlotsModal(true);
    fetchAllSlots();
  };

  // Reset slot form
  const resetSlotForm = () => {
    setSlotFormData({
      slotName: '',
      slotType: 'Pickup',
      maxCapacity: 10
    });
    setEditingSlotData(null);
    setShowAddSlotForm(false);
  };

  // Handle add new slot
  const handleAddNewSlot = () => {
    resetSlotForm();
    setShowAddSlotForm(true);
  };

  // Handle edit slot
  const handleEditSlot = (slot) => {
    setSlotFormData({
      slotName: slot.slotName,
      slotType: slot.slotType,
      maxCapacity: slot.maxCapacity
    });
    setEditingSlotData(slot);
    setShowAddSlotForm(true);
  };

  // Handle save slot (create or update)
  const handleSaveSlot = async () => {
    if (!slotFormData.slotName.trim()) {
      setModalError('Slot name is required');
      return;
    }
    if (slotFormData.maxCapacity < 1) {
      setModalError('Capacity must be at least 1');
      return;
    }

    setSavingSlot(true);
    setModalError(null);

    try {
      if (editingSlotData) {
        // Update existing slot
        await slotService.updateSlot(editingSlotData._id, {
          slotName: slotFormData.slotName,
          slotType: slotFormData.slotType,
          maxCapacity: slotFormData.maxCapacity,
        });
        setModalSuccess('Slot updated successfully!');
      } else {
        // Create new slot
        await slotService.createSlot({
          slotName: slotFormData.slotName,
          slotType: slotFormData.slotType,
          maxCapacity: slotFormData.maxCapacity,
        });
        setModalSuccess('Slot created successfully!');
      }

      setTimeout(() => setModalSuccess(null), 3000);
      resetSlotForm();
      fetchAllSlots();
      fetchSlots(); // Refresh the main view too
    } catch (err) {
      console.error('Failed to save slot:', err);
      setModalError(err.response?.data?.message || 'Failed to save slot. Please try again.');
    } finally {
      setSavingSlot(false);
    }
  };

  // Handle delete slot
  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot? This action cannot be undone.')) {
      return;
    }

    setDeletingSlotId(slotId);
    setModalError(null);

    try {
      await slotService.deleteSlot(slotId);
      setModalSuccess('Slot deleted successfully!');
      setTimeout(() => setModalSuccess(null), 3000);
      fetchAllSlots();
      fetchSlots();
    } catch (err) {
      console.error('Failed to delete slot:', err);
      setModalError(err.response?.data?.message || 'Failed to delete slot. It may be in use by existing orders.');
    } finally {
      setDeletingSlotId(null);
    }
  };

  // Handle toggle slot active status
  const handleToggleSlotActive = async (slot) => {
    try {
      await slotService.toggleSlotActive(slot._id);
      setModalSuccess(`Slot ${slot.isActive !== false ? 'deactivated' : 'activated'} successfully!`);
      setTimeout(() => setModalSuccess(null), 3000);
      fetchAllSlots();
      fetchSlots();
    } catch (err) {
      console.error('Failed to toggle slot status:', err);
      setModalError('Failed to change slot status. Please try again.');
    }
  };

  const getSlotStatus = (used, capacity) => {
    const percentage = (used / capacity) * 100;
    if (percentage >= 100) return 'full';
    if (percentage >= 80) return 'nearly-full';
    return 'available';
  };

  const handleUpdateCapacity = async (type, slotId) => {
    const newCapacity = parseInt(tempCapacity, 10);
    if (isNaN(newCapacity) || newCapacity < 0) return;

    try {
      // Save to database as a date-specific override
      await slotService.setSlotOverride(slotId, selectedDate, newCapacity);

      // Update local state
      if (type === 'pickup') {
        setPickupSlots((prev) =>
          prev.map((slot) =>
            slot.id === slotId ? { ...slot, capacity: newCapacity } : slot
          )
        );
      } else {
        setDeliverySlots((prev) =>
          prev.map((slot) =>
            slot.id === slotId ? { ...slot, capacity: newCapacity } : slot
          )
        );
      }

      setSavedSlot(`${type}-${slotId}`);
      setTimeout(() => setSavedSlot(null), 1500);
    } catch (err) {
      console.error('Failed to update capacity:', err);
      setError('Failed to update capacity. Please try again.');
    } finally {
      setEditingSlot(null);
      setTempCapacity('');
    }
  };

  const renderSlotRow = (slot, type) => {
    const status = getSlotStatus(slot.used, slot.capacity);
    const isEditing = editingSlot === `${type}-${slot.id}`;
    const isSaved = savedSlot === `${type}-${slot.id}`;

    return (
      <tr key={slot.id} className={`slot-row ${status}`}>
        <td>
          <span className="slot-name">{slot.name}</span>
        </td>
        <td>
          <span className="slot-usage">
            <span className="usage-current">{slot.used}</span>
            <span className="usage-separator">/</span>
            <span className="usage-max">{slot.capacity}</span>
          </span>
        </td>
        <td>
          <input
            type="number"
            className="capacity-input"
            value={isEditing ? tempCapacity : slot.capacity}
            onChange={(e) => {
              if (!isEditing) {
                setEditingSlot(`${type}-${slot.id}`);
                setTempCapacity(e.target.value);
              } else {
                setTempCapacity(e.target.value);
              }
            }}
            onFocus={() => {
              setEditingSlot(`${type}-${slot.id}`);
              setTempCapacity(slot.capacity.toString());
            }}
            min={1}
          />
        </td>
        <td>
          <span className={`status-badge ${status}`}>
            {status === 'full' ? 'Full' : status === 'nearly-full' ? 'Nearly Full' : 'Available'}
          </span>
        </td>
        <td className="text-center">
          <Button
            size="sm"
            variant={isSaved ? 'success' : 'primary'}
            onClick={() => handleUpdateCapacity(type, slot.id)}
            disabled={!isEditing}
          >
            {isSaved ? <Check size={16} /> : 'Update'}
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <>
      <Header
        title="Slot Status & Management"
        subtitle="View daily slot usage and manage default maximum capacity"
        onMenuClick={onMenuClick}
        actions={
          <div className="header-actions-group">
            <Button
              variant="secondary"
              icon={<Gear size={18} />}
              onClick={handleOpenEditSlots}
            >
              Edit Slots
            </Button>
            <div className="date-picker-wrapper">
              <label htmlFor="datePicker">Select Date:</label>
              <input
                type="date"
                id="datePicker"
                className="date-picker"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          </div>
        }
      />
      <div className="page-content">
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button className="banner-close-btn" onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {success && (
          <div className="success-banner">
            <span>{success}</span>
            <button className="banner-close-btn" onClick={() => setSuccess(null)}>&times;</button>
          </div>
        )}
        {loading ? (
          <div className="loading">
            <div className="spinner" />
            <span className="loading-text">Loading slots...</span>
          </div>
        ) : pickupSlots.length === 0 && deliverySlots.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>No Slots Configured</h2>
            <p style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
              You need to create time slots before you can manage them. Click the button below to create default pickup and delivery slots.
            </p>
            <Button
              icon={<Plus size={18} />}
              onClick={handleSeedSlots}
              disabled={seeding}
            >
              {seeding ? 'Creating Slots...' : 'Create Default Slots'}
            </Button>
          </div>
        ) : (
          <div className="slots-container">
            <div className="slot-section">
              <div className="slot-section-header">
                <h2>Pickup Slots</h2>
                <span className="slot-date">{formatSelectedDate(selectedDate)}</span>
              </div>
              <div className="table-container">
                <table className="slots-table">
                  <thead>
                    <tr>
                      <th>Time Slot</th>
                      <th>Usage</th>
                      <th>Max Capacity</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickupSlots.length > 0 ? (
                      pickupSlots.map((slot) => renderSlotRow(slot, 'pickup'))
                    ) : (
                      <tr>
                        <td colSpan="5" className="empty-cell">No pickup slots configured</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="slot-section">
              <div className="slot-section-header">
                <h2>Delivery Slots</h2>
                <span className="slot-date">{formatSelectedDate(selectedDate)}</span>
              </div>
              <div className="table-container">
                <table className="slots-table">
                  <thead>
                    <tr>
                      <th>Time Slot</th>
                      <th>Usage</th>
                      <th>Max Capacity</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliverySlots.length > 0 ? (
                      deliverySlots.map((slot) => renderSlotRow(slot, 'delivery'))
                    ) : (
                      <tr>
                        <td colSpan="5" className="empty-cell">No delivery slots configured</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="slot-legend">
          <div className="legend-item">
            <span className="legend-indicator available"></span>
            <span>Available</span>
          </div>
          <div className="legend-item">
            <span className="legend-indicator nearly-full"></span>
            <span>Nearly Full (80%+)</span>
          </div>
          <div className="legend-item">
            <span className="legend-indicator full"></span>
            <span>Full</span>
          </div>
        </div>
      </div>

      {/* Edit Slots Modal */}
      <Modal
        isOpen={showEditSlotsModal}
        onClose={() => {
          setShowEditSlotsModal(false);
          setModalError(null);
          setModalSuccess(null);
          resetSlotForm();
        }}
        title="Manage Slots"
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => {
            setShowEditSlotsModal(false);
            setModalError(null);
            setModalSuccess(null);
            resetSlotForm();
          }}>
            Close
          </Button>
        }
      >
        <div className="edit-slots-content">
          {modalError && (
            <div className="error-banner" style={{ marginBottom: '16px' }}>
              <span>{modalError}</span>
              <button className="banner-close-btn" onClick={() => setModalError(null)}>&times;</button>
            </div>
          )}
          {modalSuccess && (
            <div className="success-banner" style={{ marginBottom: '16px' }}>
              <span>{modalSuccess}</span>
              <button className="banner-close-btn" onClick={() => setModalSuccess(null)}>&times;</button>
            </div>
          )}
          {/* Add/Edit Slot Form */}
          {showAddSlotForm ? (
            <div className="slot-form-section">
              <div className="slot-form-header">
                <h4>{editingSlotData ? 'Edit Slot' : 'Add New Slot'}</h4>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={resetSlotForm}
                >
                  Cancel
                </Button>
              </div>
              <div className="slot-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Slot Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., 9:00 AM - 12:00 PM"
                      value={slotFormData.slotName}
                      onChange={(e) => setSlotFormData({ ...slotFormData, slotName: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Slot Type *</label>
                    <select
                      className="form-select"
                      value={slotFormData.slotType}
                      onChange={(e) => setSlotFormData({ ...slotFormData, slotType: e.target.value })}
                    >
                      <option value="Pickup">Pickup</option>
                      <option value="Delivery">Delivery</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Max Capacity *</label>
                    <input
                      type="number"
                      className="form-input"
                      min="1"
                      value={slotFormData.maxCapacity}
                      onChange={(e) => setSlotFormData({ ...slotFormData, maxCapacity: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                </div>
                <div className="slot-form-actions">
                  <Button
                    onClick={handleSaveSlot}
                    disabled={savingSlot}
                    icon={savingSlot ? null : <FloppyDisk size={16} />}
                  >
                    {savingSlot ? 'Saving...' : (editingSlotData ? 'Update Slot' : 'Create Slot')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="add-slot-button-section">
              <Button icon={<Plus size={18} />} onClick={handleAddNewSlot}>
                Add New Slot
              </Button>
            </div>
          )}

          {/* Slots List */}
          <div className="edit-slots-list">
            {loadingAllSlots ? (
              <div className="loading-inline">
                <div className="spinner small" />
                <span>Loading slots...</span>
              </div>
            ) : allSlots.length === 0 ? (
              <div className="empty-slots-message">
                <Clock size={48} />
                <p>No slots configured yet.</p>
                <p className="text-muted">Click "Add New Slot" to create your first slot.</p>
              </div>
            ) : (
              <>
                {/* Pickup Slots */}
                <div className="slot-type-section">
                  <h4 className="slot-type-title">Pickup Slots ({allSlots.filter(s => s.slotType === 'Pickup').length})</h4>
                  <div className="slots-edit-grid">
                    {allSlots
                      .filter(slot => slot.slotType === 'Pickup')
                      .map(slot => (
                        <div key={slot._id} className={`slot-edit-card ${slot.isActive === false ? 'inactive' : ''}`}>
                          <div className="slot-edit-info">
                            <span className="slot-edit-name">{slot.slotName}</span>
                            <span className="slot-edit-capacity">
                              Capacity: {slot.maxCapacity}
                            </span>
                            {slot.isActive === false && (
                              <span className="slot-inactive-badge">Inactive</span>
                            )}
                          </div>
                          <div className="slot-edit-actions">
                            <button
                              className="action-btn"
                              title="Edit"
                              onClick={() => handleEditSlot(slot)}
                            >
                              <PencilSimple size={16} />
                            </button>
                            <button
                              className={`action-btn ${slot.isActive === false ? 'success' : 'warning'}`}
                              title={slot.isActive === false ? 'Activate' : 'Deactivate'}
                              onClick={() => handleToggleSlotActive(slot)}
                            >
                              <Power size={16} />
                            </button>
                            <button
                              className="action-btn danger"
                              title="Delete"
                              onClick={() => handleDeleteSlot(slot._id)}
                              disabled={deletingSlotId === slot._id}
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    {allSlots.filter(s => s.slotType === 'Pickup').length === 0 && (
                      <p className="no-slots-text">No pickup slots configured</p>
                    )}
                  </div>
                </div>

                {/* Delivery Slots */}
                <div className="slot-type-section">
                  <h4 className="slot-type-title">Delivery Slots ({allSlots.filter(s => s.slotType === 'Delivery').length})</h4>
                  <div className="slots-edit-grid">
                    {allSlots
                      .filter(slot => slot.slotType === 'Delivery')
                      .map(slot => (
                        <div key={slot._id} className={`slot-edit-card ${slot.isActive === false ? 'inactive' : ''}`}>
                          <div className="slot-edit-info">
                            <span className="slot-edit-name">{slot.slotName}</span>
                            <span className="slot-edit-capacity">
                              Capacity: {slot.maxCapacity}
                            </span>
                            {slot.isActive === false && (
                              <span className="slot-inactive-badge">Inactive</span>
                            )}
                          </div>
                          <div className="slot-edit-actions">
                            <button
                              className="action-btn"
                              title="Edit"
                              onClick={() => handleEditSlot(slot)}
                            >
                              <PencilSimple size={16} />
                            </button>
                            <button
                              className={`action-btn ${slot.isActive === false ? 'success' : 'warning'}`}
                              title={slot.isActive === false ? 'Activate' : 'Deactivate'}
                              onClick={() => handleToggleSlotActive(slot)}
                            >
                              <Power size={16} />
                            </button>
                            <button
                              className="action-btn danger"
                              title="Delete"
                              onClick={() => handleDeleteSlot(slot._id)}
                              disabled={deletingSlotId === slot._id}
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    {allSlots.filter(s => s.slotType === 'Delivery').length === 0 && (
                      <p className="no-slots-text">No delivery slots configured</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

export default Slots;
